import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import "./work.css";

const POLL_MS = 60000;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  const mins = Math.max(0, Math.round((Date.now() - then.getTime()) / 60000));
  if (Number.isNaN(mins)) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** Opens the panel beside a sidebar row; small screens keep the default fixed position. */
function panelPositionBeside(el) {
  if (!el || window.innerWidth < 640) return null;
  const rect = el.getBoundingClientRect();
  const maxTop = window.innerHeight * 0.3 - 16;
  return { left: rect.right + 8, top: Math.max(16, Math.min(rect.top, maxTop)) };
}

/**
 * Bell with unread count; opens the latest notifications and jumps to the linked page.
 * variant="nav" renders a full sidebar row (icon, label, badge) instead of the icon button.
 */
function NotificationBell({ api, variant = "icon", className = "", label = "Alerts" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [panelStyle, setPanelStyle] = useState(null);
  const ref = useRef(null);
  const isNav = variant === "nav";

  const load = useCallback(async () => {
    try {
      const res = await api.notifications({ limit: 30 });
      if (res) setData(res);
    } catch {
      /* offline / not migrated yet: keep the bell quiet */
    }
  }, [api]);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.Is_Read) {
      api.markRead([n.Notification_Id]).then(load).catch(() => null);
    }
    if (n.Link) navigate(api.link(n.Link));
  };

  const markAll = async () => {
    await api.markRead().catch(() => null);
    load();
  };

  const badge = data.unread ? (
    <span className="wk-bell__badge">{data.unread > 99 ? "99+" : data.unread}</span>
  ) : null;

  return (
    <div className={`wk-bell${isNav ? " wk-bell--nav" : ""}`} ref={ref}>
      <button
        type="button"
        className={isNav ? `wk-bell__nav-btn ${className}`.trim() : "wk-bell__btn"}
        onClick={(e) => {
          if (!open) {
            setPanelStyle(isNav ? panelPositionBeside(e.currentTarget) : null);
            load();
          }
          setOpen((v) => !v);
        }}
        aria-label={data.unread ? `Notifications, ${data.unread} unread` : "Notifications"}
        aria-expanded={open}
        aria-haspopup="true"
        title={isNav ? label : "Notifications"}
      >
        {isNav ? (
          <>
            <span className="wk-bell__nav-icon">
              <FaBell className="nav-item-icon" aria-hidden="true" />
              {badge}
            </span>
            <span className="nav-item-label">{label}</span>
          </>
        ) : (
          <>
            <FaBell aria-hidden="true" />
            {badge}
          </>
        )}
      </button>
      {open ? (
        <div className="wk-bell__panel" role="dialog" aria-label="Notifications" style={panelStyle || undefined}>
          <div className="wk-bell__head">
            <span>Notifications</span>
            {data.unread ? (
              <button type="button" className="dmp-link" onClick={markAll} style={{ fontSize: 12 }}>
                Mark all read
              </button>
            ) : null}
          </div>
          {data.items.length ? (
            data.items.map((n) => (
              <button
                key={n.Notification_Id}
                type="button"
                className={`wk-bell__item${n.Is_Read ? "" : " is-unread"}`}
                onClick={() => openItem(n)}
              >
                <div className="wk-bell__item-title">{n.Title}</div>
                {n.Body ? <div className="wk-bell__item-body">{n.Body}</div> : null}
                <div className="wk-bell__item-when">
                  {timeAgo(n.Created_On)}
                  {n.Email_Status === "sent" ? " · emailed" : ""}
                </div>
              </button>
            ))
          ) : (
            <div className="wk-empty">
              <div className="wk-empty__title">You're all caught up</div>
            </div>
          )}
          <button
            type="button"
            className="wk-bell__item"
            style={{ textAlign: "center", fontWeight: 600 }}
            onClick={() => {
              setOpen(false);
              navigate(api.link("my-work"));
            }}
          >
            Open my work
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default NotificationBell;
